/**
 * @module api-client
 * @description Core HTTP utility module providing centralized request handling with authentication and error management.
 */

import type { HttpConfig, RequestOptions, TokenRefreshResult } from "../types";
import {
  clearAuthTokens,
  getAccessToken,
  getAuthHeader,
  getRefreshToken,
  setAuthTokens,
} from "./auth-storage";
import {
  createHttpError,
  parseErrorResponse,
  parseFetchError,
  showErrorToast,
  showSuccessToast,
} from "./error-handler";
import { Sentry } from "@/lib/sentry";

/**
 * Default configuration for HTTP requests.
 * Sets base URL and default headers for all requests.
 */
const DEFAULT_CONFIG: HttpConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  showErrorToast: true,
  showSuccessToast: false,
};

/**
 * Shared in-flight refresh promise.
 * Parallel 401s must await the same refresh rather than each triggering a new one —
 * otherwise the first caller rotates the refresh token and the rest fail with
 * "invalid refresh token" and land in FeatureErrorBoundary's "Something went wrong".
 */
let refreshPromise: Promise<TokenRefreshResult> | null = null;

/**
 * Builds complete URL from base URL and endpoint.
 * Returns absolute URLs unchanged, otherwise prepends base URL.
 * @param url - The endpoint URL or absolute URL
 * @returns Complete URL for the request
 */
export function buildUrl(url: string): string {
  // Return absolute URLs unchanged
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Get base URL and clean trailing/leading slashes
  const baseURL = DEFAULT_CONFIG.baseURL || "";
  const cleanBase = baseURL.replace(/\/$/, "");
  const cleanUrl = url.replace(/^\//, "");

  // Combine base URL with endpoint
  return cleanBase ? `${cleanBase}/${cleanUrl}` : `/${cleanUrl}`;
}

/**
 * Refreshes authentication token using refresh token.
 * Updates stored tokens on success or clears them on failure.
 * @returns Promise with refresh result containing success status and tokens or error
 */
async function refreshAuthToken(): Promise<TokenRefreshResult> {
  // Get stored refresh token
  const refreshToken = getRefreshToken();

  // Return error if no refresh token available
  if (!refreshToken) {
    return { success: false, error: "No refresh token available" };
  }

  try {
    // Make refresh request to auth endpoint
    const response = await fetch(buildUrl("/auth/refresh"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    // Check if refresh request was successful
    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    // Parse response data
    const data = await response.json();

    // Validate response contains required token fields
    if (data.accessToken && data.refreshToken && data.expiresAt) {
      // Create tokens object from response
      const tokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        refreshExpiresAt: data.refreshExpiresAt, // Include refresh token expiry if available
      };

      // Store new tokens
      setAuthTokens(tokens);
      return { success: true, tokens };
    }

    // Throw error for invalid response format
    throw new Error("Invalid token refresh response");
  } catch (error) {
    // Clear tokens on refresh failure
    clearAuthTokens();
    // Return error result with message
    return {
      success: false,
      error: error instanceof Error ? error.message : "Token refresh failed",
    };
  }
}

/**
 * Routes that must render for a signed-out visitor. A failed token refresh must
 * not bounce them to /signin: an approver opening an emailed approval link is
 * authorized by the link token alone and may have no account at all, yet any
 * stale Taqat token left in their browser makes app-init call /auth/session,
 * 401, fail to refresh, and hard-redirect them off the page they were sent to.
 */
const PUBLIC_PATHS = [
  "/approve",
  "/invitation",
  "/link",
  "/signin",
  "/signup",
  "/forgot-password",
  "/verify-otp",
  "/resources",
  "/employee",
];

/** True when the current URL is a route a signed-out visitor is meant to see. */
function isOnPublicPath(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Handles authentication errors by refreshing token and retrying request.
 * All concurrent 401s share the same refresh promise so they can't rotate the refresh
 * token out from under each other; on failure, clears tokens and redirects to sign-in.
 * @param url - The original request URL
 * @param options - The original request options
 * @returns Promise with response data after successful retry
 * @throws HttpError if refresh fails
 */
async function handleAuthenticationError<T>(
  url: string,
  options: RequestOptions
): Promise<T> {
  // Reuse the in-flight refresh promise if one exists, otherwise start a new one.
  // The finally clears the slot so subsequent 401s after completion kick off a fresh refresh.
  if (!refreshPromise) {
    refreshPromise = refreshAuthToken().finally(() => {
      refreshPromise = null;
    });
  }

  const refreshResult = await refreshPromise;

  // On success, retry the original request with the new token.
  // The `true` marks this as the post-refresh retry: if it still returns 401,
  // makeRequest surfaces the error instead of refreshing again (see below).
  if (refreshResult.success) {
    return await makeRequest(url, options, true);
  }

  // Refresh failed — clear the dead tokens either way, but only bounce to
  // sign-in from routes that actually require an account. On a public page the
  // stale token is incidental; redirecting would strand a visitor who never
  // needed to be signed in.
  clearAuthTokens();

  if (typeof window !== "undefined" && !isOnPublicPath()) {
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl = encodeURIComponent(currentPath);
    window.location.href = `/signin?redirectUrl=${redirectUrl}`;
  }

  throw createHttpError(
    "authentication",
    refreshResult.error || "Session expired. Please sign in again."
  );
}

/**
 * Makes HTTP request with error handling and authentication.
 * Handles token refresh on 401 errors and shows toast notifications.
 * @param url - The request URL endpoint
 * @param options - Request configuration options
 * @returns Promise with response data
 * @throws HttpError for request failures
 */
async function makeRequest(
  url: string,
  options: RequestOptions = {},
  // Internal flag set by handleAuthenticationError when retrying after a token
  // refresh. It bounds the refresh→retry cycle to a single attempt so a 401 that
  // a refresh can't fix (e.g. WORKSPACE_NOT_FOUND for an authenticated user with
  // no workspace) surfaces as an error instead of looping forever.
  isRetry = false
) {
  // Merge default config with provided options
  const config = {
    ...DEFAULT_CONFIG,
    ...options,
    headers: {
      ...DEFAULT_CONFIG.headers,
      ...options.headers,
    },
  };

  // Guard: Block requests when auth is required but no token exists
  // This prevents request loops when user is not authenticated
  if (config.requiresAuth !== false) {
    const token = getAccessToken();
    if (!token) {
      // No token available - throw error to prevent request loop
      throw createHttpError(
        "authentication",
        "Not authenticated - please sign in"
      );
    }
  }

  // Add authentication header if required
  if (config.requiresAuth !== false) {
    // Always use Bearer token authentication
    const authHeader = getAuthHeader();
    // Apply auth header if available
    if (authHeader) {
      config.headers = {
        ...config.headers,
        Authorization: authHeader,
      };
    }
  }

  // Build complete URL for request
  const fullUrl = buildUrl(url);

  try {
    // Create fetch options object
    const fetchOptions: RequestInit = {
      method: config.method || "GET",
      headers: config.headers,
    };

    // Add request body for non-GET requests
    if (config.body && config.method !== "GET") {
      // Handle FormData specially
      if (config.body instanceof FormData) {
        // Assign FormData directly
        fetchOptions.body = config.body;
        // Remove Content-Type to let browser set boundary
        if (fetchOptions.headers && typeof fetchOptions.headers === "object") {
          const headers = fetchOptions.headers as Record<string, string>;
          delete headers["Content-Type"];
        }
      } else if (
        config.headers?.["Content-Type"]?.includes("application/json")
      ) {
        // Stringify JSON bodies
        fetchOptions.body = JSON.stringify(config.body);
      } else {
        // Pass other body types as-is
        fetchOptions.body = config.body as BodyInit;
      }
    }

    // Execute HTTP request
    const response = await fetch(fullUrl, fetchOptions);

    // Check for token refresh headers from auth middleware.
    // Server sets these when it proactively refreshes tokens.
    // The expiry headers are preferred; if missing we skip the update rather than
    // hardcode a TTL, because a wrong expiry silently fools isTokenValid() and
    // causes 401 storms when the real token has already expired server-side.
    const newAccessToken = response.headers.get('X-New-Access-Token');
    const newRefreshToken = response.headers.get('X-New-Refresh-Token');
    const newExpiresAtHeader = response.headers.get('X-New-Expires-At');
    const newRefreshExpiresAtHeader = response.headers.get('X-New-Refresh-Expires-At');

    if (newAccessToken && newRefreshToken && newExpiresAtHeader) {
      const parsedExpiresAt = Number(newExpiresAtHeader);
      const parsedRefreshExpiresAt = newRefreshExpiresAtHeader
        ? Number(newRefreshExpiresAtHeader)
        : undefined;

      if (Number.isFinite(parsedExpiresAt)) {
        setAuthTokens({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
          expiresAt: parsedExpiresAt,
          refreshExpiresAt:
            parsedRefreshExpiresAt && Number.isFinite(parsedRefreshExpiresAt)
              ? parsedRefreshExpiresAt
              : undefined,
        });
      }
    }

    // Check for authentication errors.
    // Only refresh+retry once: a 401 that persists after a successful refresh
    // (isRetry) isn't a stale token, so fall through and surface the error
    // rather than triggering another refresh — which would loop indefinitely.
    if (response.status === 401 && config.requiresAuth !== false && !isRetry) {
      // Handle 401 by refreshing token and retrying
      return await handleAuthenticationError(url, options);
    }

    // Parse response based on content type
    let responseData;
    const contentType = response.headers.get("content-type");

    // Parse as JSON or text based on content type
    if (contentType?.includes("application/json")) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      // Parse error from response
      const error = parseErrorResponse(response, responseData);

      // Add Sentry breadcrumb for API errors
      Sentry.addBreadcrumb({
        category: 'api',
        message: `${config.method || 'GET'} ${url} — ${response.status}`,
        level: response.status >= 500 ? 'error' : 'warning',
        data: { status: response.status, url: fullUrl },
      });

      // Capture 5xx server errors as Sentry exceptions
      if (response.status >= 500) {
        Sentry.captureException(error, {
          extra: { url: fullUrl, status: response.status, method: config.method || 'GET' },
        });
      }

      // Show error toast if enabled
      if (config.showErrorToast) {
        showErrorToast(error);
      }

      // Throw parsed error
      throw error;
    }

    // Show success toast if enabled
    if (config.showSuccessToast) {
      // Extract message from response or use default
      const successMessage =
        ((responseData as Record<string, unknown>)?.message as string) ||
        "Operation completed successfully";
      showSuccessToast(successMessage);
    }

    // Return response data
    return responseData ;
  } catch (error) {
    // Check if already an HttpError
    if (error instanceof Error && "type" in error) {
      // Re-throw existing HttpError
      throw error;
    }

    // Parse fetch error into HttpError
    const httpError = parseFetchError(error as Error);

    // Show error toast if enabled
    if (config.showErrorToast) {
      showErrorToast(httpError);
    }

    // Throw parsed error
    throw httpError;
  }
}

/**
 * Main API request function for making HTTP requests.
 * Wraps makeRequest with public interface.
 * @param url - The request URL endpoint
 * @param options - Request configuration options
 * @returns Promise with typed response data
 */
export async function apiRequest(
  url: string,
  options: RequestOptions = {}
) {
  return makeRequest(url, options);
}

/**
 * Explicit token refresh function that can be called directly.
 * Uses the same refresh logic as the internal refreshAuthToken but exposes it publicly.
 * @returns Promise with refresh result containing success status and tokens or error
 */
export async function refreshTokens(): Promise<TokenRefreshResult> {
  return refreshAuthToken();
}

/**
 * Convenience methods for different HTTP verbs.
 * Provides typed, method-specific request functions.
 */
export const api = {
  /**
   * Makes GET request to specified endpoint.
   * Retrieves data without side effects.
   * @param url - The endpoint URL
   * @param options - Additional request options
   * @returns Promise with typed response data
   */
  get: (url: string, options?: Omit<RequestOptions, "method">) =>
    apiRequest(url, { ...options, method: "GET" }),

  /**
   * Makes POST request with data payload.
   * Creates new resources on the server.
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param options - Additional request options
   * @returns Promise with typed response data
   */
  post: (
    url: string,
    data?: unknown,
    options?: Omit<RequestOptions, "method" | "body">
  ) => apiRequest(url, { ...options, method: "POST", body: data }),

  /**
   * Makes PUT request to replace resource.
   * Updates entire resource with provided data.
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param options - Additional request options
   * @returns Promise with typed response data
   */
  put: (
    url: string,
    data?: unknown,
    options?: Omit<RequestOptions, "method" | "body">
  ) => apiRequest(url, { ...options, method: "PUT", body: data }),

  /**
   * Makes PATCH request for partial updates.
   * Updates specific fields of a resource.
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param options - Additional request options
   * @returns Promise with typed response data
   */
  patch: (
    url: string,
    data?: unknown,
    options?: Omit<RequestOptions, "method" | "body">
  ) => apiRequest(url, { ...options, method: "PATCH", body: data }),

  /**
   * Makes DELETE request to remove resource.
   * Removes specified resource from server.
   * @param url - The endpoint URL
   * @param options - Additional request options
   * @returns Promise with typed response data
   */
  delete: (url: string, options?: Omit<RequestOptions, "method">) =>
    apiRequest(url, { ...options, method: "DELETE" }),
};
